import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sun, Eyeglasses, Drop, DeviceMobile, Ear, ArrowLeft, ArrowClockwise, X, CaretRight, House, Plus, Check, PaperPlaneTilt, Heart, Sparkle, ArrowRight } from '@phosphor-icons/react';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import { ProductCard } from './components/ProductCard';
import { getBrandLogo } from './components/brandAssets';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORE_SLUG = 'mrg';

const TYPE_ICONS = {
  soleil: <Sun size={40} weight="fill" />,
  vue: <Eyeglasses size={40} weight="fill" />,
  lentille: <Drop size={40} weight="fill" />,
  connect: <DeviceMobile size={40} weight="fill" />,
  audio: <Ear size={40} weight="fill" />,
};
const typeIcon = (slug = '') => {
  const k = Object.keys(TYPE_ICONS).find(x => slug.includes(x));
  return k ? TYPE_ICONS[k] : <Eyeglasses size={40} weight="fill" />;
};

const KioskPage = () => {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();

  const [store, setStore] = useState(null);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [step, setStep] = useState('type'); // type | gender | brand | results
  const [selType, setSelType] = useState(null);
  const [selGender, setSelGender] = useState(null);
  const [selBrand, setSelBrand] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selection, setSelection] = useState([]);
  const [showFinish, setShowFinish] = useState(false);
  const [kName, setKName] = useState('');
  const [kPhone, setKPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [submitErr, setSubmitErr] = useState('');
  const [reqNumber, setReqNumber] = useState('');
  const [questions, setQuestions] = useState([]);
  const [qIndex, setQIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [fromAssistant, setFromAssistant] = useState(false);

  const startAssistant = () => { setQIndex(0); setAnswers([]); setFromAssistant(true); setStep('assistant'); };
  const finishAssistant = (opts) => {
    setLoading(true); setStep('results'); setSelType(null); setSelGender(null); setSelBrand(null);
    axios.post(`${API_URL}/api/s/${STORE_SLUG}/kiosk/recommend`, { options: opts })
      .then(r => setProducts(r.data?.products || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  };
  const chooseOption = (opt) => {
    const next = [...answers, opt];
    setAnswers(next);
    if (qIndex < questions.length - 1) setQIndex(qIndex + 1);
    else finishAssistant(next);
  };

  const toggleSelect = (p) => {
    setSelection(prev => prev.some(s => s.id === p.id) ? prev.filter(s => s.id !== p.id) : [...prev, p]);
  };

  const submitSelection = async () => {
    setSubmitErr('');
    if (!kName.trim()) { setSubmitErr('Merci d’indiquer votre prénom.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/s/${STORE_SLUG}/kiosk/request`, {
        name: kName.trim(), phone: kPhone.trim(),
        items: selection.map(s => ({ id: s.id, name: s.name, price: s.price, image: s.featured_image, slug: s.slug })),
      });
      setReqNumber(res.data?.request?.request_number || '');
      setDone(true);
    } catch (e) {
      setSubmitErr(e?.response?.data?.detail || 'Une erreur est survenue.');
    } finally {
      setSubmitting(false);
    }
  };

  const newSearch = () => {
    setSelection([]); setShowFinish(false); setDone(false); setKName(''); setKPhone('');
    setStep('type'); setSelType(null); setSelGender(null); setSelBrand(null); setProducts([]);
    setFromAssistant(false); setAnswers([]); setQIndex(0);
  };

  useEffect(() => {
    axios.get(`${API_URL}/api/s/${STORE_SLUG}`).then(r => setStore(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/s/${STORE_SLUG}/categories`).then(r => setCategories(r.data || [])).catch(() => {});
    axios.get(`${API_URL}/api/s/${STORE_SLUG}/brands?with_image=false`).then(r => setBrands(r.data || [])).catch(() => {});
    axios.get(`${API_URL}/api/s/${STORE_SLUG}/kiosk/questions`).then(r => setQuestions(r.data?.questions || [])).catch(() => {});
  }, []);

  const colors = useMemo(() => {
    const id = store?.identity || {};
    return { primary: id.primary_color || '#111111', accent: id.accent_color || '#FF004D', dark: '#111111' };
  }, [store]);
  const accent = colors.accent;

  const typeCats = useMemo(() => {
    const tops = categories.filter(c => !c.parent_id && !/marques|uncateg/i.test(c.slug || '') && !/marques|uncateg/i.test(c.name || ''));
    const order = ['soleil', 'vue', 'lentille', 'connect'];
    return tops.sort((a, b) => {
      const ai = order.findIndex(o => (a.slug || '').includes(o));
      const bi = order.findIndex(o => (b.slug || '').includes(o));
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
  }, [categories]);

  const genderCats = useMemo(() => selType ? categories.filter(c => c.parent_id === selType.id) : [], [categories, selType]);

  const fetchProducts = (typeCat, brand) => {
    setLoading(true); setProducts([]);
    const cat = typeCat;
    const url = brand
      ? `${API_URL}/api/s/${STORE_SLUG}/brands/${brand.slug}?type=${encodeURIComponent(cat.slug)}&limit=80`
      : `${API_URL}/api/s/${STORE_SLUG}/products?category=${cat.id}&limit=80`;
    axios.get(url)
      .then(r => setProducts(r.data?.products || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  };

  const pickType = (t) => {
    setSelType(t); setSelGender(null); setSelBrand(null);
    const kids = categories.filter(c => c.parent_id === t.id);
    if (kids.length) setStep('gender');
    else setStep('brand');
  };
  const pickGender = (g) => { setSelGender(g); setStep('brand'); };
  const pickBrand = (b) => {
    setSelBrand(b);
    fetchProducts(selGender || selType, b);
    setStep('results');
  };
  const skipBrand = () => { setSelBrand(null); fetchProducts(selGender || selType, null); setStep('results'); };

  // Quick switching from the top bar (no back-and-forth)
  const switchType = (t) => { if (selType?.id === t.id) return; setSelType(t); setSelGender(null); fetchProducts(t, selBrand); setStep('results'); };
  const switchGender = (g) => { setSelGender(g); fetchProducts(g || selType, selBrand); };
  const clearBrand = () => { setSelBrand(null); fetchProducts(selGender || selType, null); };

  const restart = () => { setStep('type'); setSelType(null); setSelGender(null); setSelBrand(null); setProducts([]); setFromAssistant(false); setAnswers([]); setQIndex(0); };
  const back = () => {
    if (step === 'results') { if (fromAssistant) { setFromAssistant(false); setStep('type'); } else setStep('brand'); }
    else if (step === 'brand') setStep(genderCats.length ? 'gender' : 'type');
    else if (step === 'gender') setStep('type');
    else if (step === 'assistant') setStep('type');
  };

  const cardProps = { colors, categories, getLink: (path) => path, isInWishlist, toggleWishlist, store, addItem, storeSlug: STORE_SLUG, isPro: false, kioskMode: true };
  const logo = store?.identity?.logo_url;

  const crumbText = [selType?.name, selGender?.name, selBrand?.name].filter(Boolean).join(' · ') || 'Catalogue';
  const crumb = (
    <span className="text-[12.5px] md:text-[15px] font-medium text-white/85 truncate">{crumbText}</span>
  );

  return (
    <div className="fixed inset-0 z-[200] bg-[#f4f5f7] flex flex-col" style={{ fontFamily: "'Poppins', sans-serif" }} data-testid="kiosk-page">
      {/* Top bar */}
      <header className="shrink-0 text-white" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${accent})` }}>
        <div className="px-4 md:px-6 py-2.5 md:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1">
            {logo ? <img src={logo} alt="MRG" className="h-6 md:h-9 w-auto object-contain brightness-0 invert shrink-0" /> : <span className="text-lg font-bold shrink-0">MRG</span>}
            <span className="hidden md:inline text-white/40 shrink-0">|</span>
            {crumb}
          </div>
          <div className="flex items-center gap-1.5 md:gap-2 shrink-0">
            {step !== 'type' && (
              <button onClick={restart} className="px-3 md:px-4 py-2 md:py-2.5 rounded-full bg-white/15 hover:bg-white/25 text-[13px] md:text-[14px] font-semibold flex items-center gap-1.5 md:gap-2" aria-label="Recommencer"><ArrowClockwise size={16} weight="bold" /> <span className="hidden md:inline">Recommencer</span></button>
            )}
            <button onClick={() => navigate('/')} className="px-3 md:px-4 py-2 md:py-2.5 rounded-full bg-white text-[13px] md:text-[14px] font-semibold flex items-center gap-1.5 md:gap-2" style={{ color: colors.primary }} data-testid="kiosk-exit" aria-label="Quitter"><X size={16} weight="bold" /> <span className="hidden sm:inline">Quitter</span></button>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-5 md:py-10">
          {step !== 'type' && (
            <button onClick={back} className="inline-flex items-center gap-2 text-[15px] font-semibold text-slate-500 hover:text-slate-800 mb-6"><ArrowLeft size={18} weight="bold" /> Retour</button>
          )}

          {/* STEP TYPE */}
          {step === 'type' && (
            <div data-testid="kiosk-step-type">
              <h1 className="text-[32px] md:text-[44px] font-semibold text-slate-900 text-center mb-2">Que recherchez-vous&nbsp;?</h1>
              <p className="text-center text-slate-500 mb-8 text-[16px]">Touchez une catégorie ou laissez-vous guider</p>
              {questions.length > 0 && (
                <button onClick={startAssistant} className="w-full mb-8 rounded-3xl p-5 md:p-7 flex items-center justify-between gap-4 text-left active:scale-[0.99] transition-transform" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${accent})` }} data-testid="kiosk-start-assistant">
                  <div className="flex items-center gap-4 min-w-0">
                    <span className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-white/15 flex items-center justify-center text-white shrink-0"><Sparkle size={26} weight="fill" /></span>
                    <div className="min-w-0">
                      <div className="text-white text-[18px] md:text-[24px] font-semibold">Laissez-vous guider ✨</div>
                      <div className="text-white/80 text-[13px] md:text-[14px]">Quelques questions et on vous propose une sélection sur-mesure</div>
                    </div>
                  </div>
                  <ArrowRight size={24} weight="bold" className="text-white shrink-0" />
                </button>
              )}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                {typeCats.map(t => (
                  <button key={t.id} onClick={() => pickType(t)}
                    className="group bg-white rounded-3xl p-8 shadow-sm hover:shadow-2xl active:scale-95 transition-all flex flex-col items-center justify-center min-h-[220px] border-2 border-transparent"
                    style={{ borderColor: 'transparent' }} data-testid={`kiosk-type-${t.slug}`}>
                    <span className="w-20 h-20 rounded-full flex items-center justify-center text-white mb-5 group-hover:scale-110 transition-transform" style={{ backgroundColor: accent }}>{typeIcon(t.slug)}</span>
                    <span className="text-[20px] font-semibold text-slate-900 text-center">{t.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP GENDER */}
          {step === 'gender' && (
            <div data-testid="kiosk-step-gender">
              <h1 className="text-[28px] md:text-[40px] font-semibold text-slate-900 text-center mb-8">Pour qui&nbsp;?</h1>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-3xl mx-auto">
                {genderCats.map(g => (
                  <button key={g.id} onClick={() => pickGender(g)}
                    className="bg-white rounded-3xl p-8 shadow-sm hover:shadow-2xl active:scale-95 transition-all text-center min-h-[150px] flex items-center justify-center"
                    data-testid={`kiosk-gender-${g.slug}`}>
                    <span className="text-[22px] font-semibold text-slate-900">{g.name.replace(/lunettes de (soleil|vue)/i, '').trim() || g.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP BRAND */}
          {step === 'brand' && (
            <div data-testid="kiosk-step-brand">
              <h1 className="text-[28px] md:text-[40px] font-semibold text-slate-900 text-center mb-8">Une marque en particulier&nbsp;?</h1>
              <div className="flex justify-center mb-6">
                <button onClick={skipBrand} className="px-8 py-4 rounded-full text-white text-[16px] font-semibold active:scale-95 transition-transform" style={{ backgroundColor: colors.primary }} data-testid="kiosk-allbrands">
                  Voir tous les modèles →
                </button>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-4">
                {brands.map(b => {
                  const blogo = b.logo_url || getBrandLogo(b.name);
                  return (
                    <button key={b.id} onClick={() => pickBrand(b)}
                      className="bg-white rounded-2xl p-4 shadow-sm hover:shadow-xl active:scale-95 transition-all flex flex-col items-center justify-center min-h-[110px]"
                      data-testid={`kiosk-brand-${b.slug}`}>
                      {blogo ? <img src={blogo} alt={b.name} className="max-h-10 max-w-[80%] object-contain mb-1" /> : null}
                      <span className="text-[12px] font-semibold text-slate-700 text-center">{b.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP ASSISTANT */}
          {step === 'assistant' && questions[qIndex] && (
            <div className="max-w-2xl mx-auto" data-testid="kiosk-assistant">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[13px] font-semibold" style={{ color: accent }}>Question {qIndex + 1} / {questions.length}</span>
                <button onClick={() => { if (qIndex > 0) { setQIndex(qIndex - 1); setAnswers(answers.slice(0, -1)); } else { setStep('type'); setFromAssistant(false); } }} className="text-[13px] text-slate-400 hover:text-slate-700 flex items-center gap-1"><ArrowLeft size={14} weight="bold" /> Retour</button>
              </div>
              <div className="h-1.5 bg-slate-200 rounded-full mb-7 overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${(qIndex / questions.length) * 100}%`, backgroundColor: accent }} /></div>
              <h1 className="text-[26px] md:text-[36px] font-semibold text-slate-900 text-center mb-8">{questions[qIndex].question}</h1>
              <div className="grid sm:grid-cols-2 gap-4">
                {(questions[qIndex].options || []).map((opt, i) => (
                  <button key={i} onClick={() => chooseOption(opt)} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl active:scale-95 transition-all text-left border-2 border-transparent hover:border-slate-200 min-h-[88px] flex items-center" data-testid={`kiosk-opt-${i}`}>
                    <span className="text-[17px] font-semibold text-slate-900">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP RESULTS */}
          {step === 'results' && (
            <div data-testid="kiosk-step-results">
              {/* Quick navigation: familles + genre */}
              <div className="mb-5 space-y-3" data-testid="kiosk-quicknav">
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {typeCats.map(t => (
                    <button key={t.id} onClick={() => switchType(t)}
                      className="shrink-0 px-4 py-2.5 rounded-full text-[14px] font-semibold border-2 transition-all active:scale-95"
                      style={selType?.id === t.id ? { backgroundColor: accent, color: '#fff', borderColor: accent } : { borderColor: '#e5e7eb', color: '#475569', backgroundColor: '#fff' }}
                      data-testid={`kiosk-nav-type-${t.slug}`}>
                      {t.name}
                    </button>
                  ))}
                </div>
                {genderCats.length > 0 && (
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    <button onClick={() => switchGender(null)} className="shrink-0 px-3.5 py-2 rounded-full text-[13px] font-medium border-2 active:scale-95"
                      style={!selGender ? { backgroundColor: colors.primary, color: '#fff', borderColor: colors.primary } : { borderColor: '#e5e7eb', color: '#64748b', backgroundColor: '#fff' }}>Tous</button>
                    {genderCats.map(g => (
                      <button key={g.id} onClick={() => switchGender(g)} className="shrink-0 px-3.5 py-2 rounded-full text-[13px] font-medium border-2 active:scale-95"
                        style={selGender?.id === g.id ? { backgroundColor: colors.primary, color: '#fff', borderColor: colors.primary } : { borderColor: '#e5e7eb', color: '#64748b', backgroundColor: '#fff' }}
                        data-testid={`kiosk-nav-gender-${g.slug}`}>
                        {g.name.replace(/lunettes de (soleil|vue)/i, '').trim() || g.name}
                      </button>
                    ))}
                  </div>
                )}
                {selBrand && (
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] text-slate-500">Marque&nbsp;:</span>
                    <span className="px-3 py-1.5 rounded-full text-[13px] font-semibold text-white inline-flex items-center gap-2" style={{ backgroundColor: accent }}>{selBrand.name} <button onClick={clearBrand} aria-label="Retirer la marque"><X size={13} weight="bold" /></button></span>
                  </div>
                )}
              </div>

              <h1 className="text-[24px] md:text-[32px] font-semibold text-slate-900 mb-1">
                {fromAssistant ? 'Notre sélection pour vous' : (selBrand ? `${selBrand.name}` : 'Tous les modèles')} <span className="text-slate-400 font-normal text-[18px]">· {products.length} modèles</span>
              </h1>
              <p className="text-slate-500 mb-6">{fromAssistant ? "D'après vos réponses — affinez avec les filtres ci-dessus" : `${selType?.name || ''}${selGender ? ` · ${selGender.name}` : ''}`}</p>
              {loading ? (
                <div className="py-24 flex justify-center"><div className="w-10 h-10 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: accent }} /></div>
              ) : products.length === 0 ? (
                <div className="py-20 text-center text-slate-400 text-[16px]">Aucun modèle pour cette sélection. <button onClick={back} className="font-semibold underline" style={{ color: accent }}>Modifier</button></div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pb-28">
                  {products.map(p => {
                    const sel = selection.some(s => s.id === p.id);
                    return (
                      <div key={p.id} className="relative rounded-2xl transition-all" style={sel ? { outline: `3px solid ${accent}`, outlineOffset: '2px' } : {}}>
                        <ProductCard p={p} {...cardProps} />
                        {/* full-card selection toggle (no navigation in kiosk) */}
                        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSelect(p); }}
                          className="absolute inset-0 z-10" aria-label={sel ? 'Retirer de ma sélection' : 'Ajouter à ma sélection'}
                          data-testid={`kiosk-card-${p.id}`} />
                        <span className="absolute top-2 right-2 z-20 w-11 h-11 rounded-full flex items-center justify-center shadow-lg border-2 pointer-events-none"
                          style={sel ? { backgroundColor: accent, color: '#fff', borderColor: accent } : { backgroundColor: '#fff', color: accent, borderColor: '#fff' }}>
                          {sel ? <Check size={20} weight="bold" /> : <Plus size={20} weight="bold" />}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer hint */}
      <footer className="shrink-0 bg-white border-t border-slate-100 px-6 py-3 text-center text-[13px] text-slate-400">
        <button onClick={() => navigate('/')} className="inline-flex items-center gap-1.5 hover:text-slate-700"><House size={14} weight="fill" /> MRG Optique &amp; Audition — Borne catalogue</button>
      </footer>

      {/* Floating selection bar */}
      {step === 'results' && selection.length > 0 && !showFinish && (
        <div className="fixed bottom-0 left-0 right-0 z-[215] bg-white border-t border-slate-200 shadow-2xl px-5 md:px-8 py-4 flex items-center justify-between gap-4" data-testid="kiosk-selbar">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-full flex items-center justify-center text-white shrink-0" style={{ backgroundColor: accent }}><Heart size={20} weight="fill" /></span>
            <div>
              <div className="text-[16px] font-semibold text-slate-900">{selection.length} paire{selection.length > 1 ? 's' : ''} sélectionnée{selection.length > 1 ? 's' : ''}</div>
              <button onClick={() => setSelection([])} className="text-[12px] text-slate-400 hover:text-rose-500 font-medium">Effacer la sélection</button>
            </div>
          </div>
          <button onClick={() => setShowFinish(true)} className="px-7 py-3.5 rounded-full text-white text-[15px] font-semibold flex items-center gap-2 active:scale-95 shrink-0" style={{ backgroundColor: accent }} data-testid="kiosk-finish-btn">
            Voir &amp; envoyer ma sélection <PaperPlaneTilt size={17} weight="fill" />
          </button>
        </div>
      )}

      {/* Finish modal */}
      {showFinish && (
        <div className="fixed inset-0 z-[220] bg-black/60 flex items-center justify-center p-4" data-testid="kiosk-finish-modal">
          <div className="bg-white rounded-3xl p-7 md:p-9 max-w-md w-full max-h-[90vh] overflow-y-auto">
            {done ? (
              <div className="text-center">
                <span className="w-16 h-16 rounded-full flex items-center justify-center text-white mx-auto mb-4" style={{ backgroundColor: accent }}><Check size={34} weight="bold" /></span>
                <h2 className="text-[24px] font-semibold text-slate-900">Merci {kName}&nbsp;!</h2>
                {reqNumber && <div className="mt-3 inline-block px-4 py-2 rounded-full text-[15px] font-bold" style={{ backgroundColor: accent + '14', color: accent }}>Demande n° {reqNumber}</div>}
                <p className="text-slate-500 mt-3">Un conseiller arrive vers vous avec votre sélection de {selection.length} paire{selection.length > 1 ? 's' : ''}.</p>
                <button onClick={newSearch} className="mt-6 px-7 py-3.5 rounded-full text-white font-semibold" style={{ backgroundColor: colors.primary }}>Nouvelle recherche</button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-1">
                  <h2 className="text-[20px] font-semibold text-slate-900">Votre sélection</h2>
                  <button onClick={() => setShowFinish(false)} className="text-slate-400 hover:text-slate-700"><X size={22} weight="bold" /></button>
                </div>
                <p className="text-slate-500 text-[14px] mb-4">{selection.length} paire{selection.length > 1 ? 's' : ''} — laissez votre prénom, un vendeur vous rejoint avec les modèles.</p>
                <div className="space-y-2 mb-5 max-h-[180px] overflow-y-auto">
                  {selection.map(s => (
                    <div key={s.id} className="flex items-center gap-3 bg-slate-50 rounded-xl p-2">
                      <div className="w-12 h-12 rounded-lg bg-white overflow-hidden flex items-center justify-center shrink-0">{s.featured_image && <img src={s.featured_image} alt="" className="w-full h-full object-contain p-1" />}</div>
                      <span className="text-[13px] text-slate-700 flex-1 line-clamp-2">{s.name}</span>
                      <button onClick={() => toggleSelect(s)} className="text-slate-300 hover:text-rose-500"><X size={16} weight="bold" /></button>
                    </div>
                  ))}
                </div>
                <input value={kName} onChange={e => setKName(e.target.value)} placeholder="Votre prénom *" className="w-full h-12 px-4 rounded-xl border border-slate-200 outline-none focus:border-slate-400 text-[15px] mb-3" data-testid="kiosk-name" />
                <input value={kPhone} onChange={e => setKPhone(e.target.value)} placeholder="Téléphone (optionnel)" className="w-full h-12 px-4 rounded-xl border border-slate-200 outline-none focus:border-slate-400 text-[15px] mb-3" data-testid="kiosk-phone" />
                {submitErr && <p className="text-rose-600 text-[13px] mb-3" data-testid="kiosk-err">{submitErr}</p>}
                <button onClick={submitSelection} disabled={submitting} className="w-full py-4 rounded-full text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50" style={{ backgroundColor: accent }} data-testid="kiosk-submit">
                  {submitting ? 'Envoi…' : 'Envoyer ma sélection au vendeur'} <PaperPlaneTilt size={18} weight="fill" />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default KioskPage;
